import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { fmtMoney, fmtDate } from '../lib/format';
import { PlatformFeeExplainer, PlatformFeeAgreement, StripeHandoffNote } from './PlatformFeeTerms';

// Getting paid online through QPS Hub, in the technician portal.
//
// Two things: the switch (connect a Stripe account so customers can pay by
// card or bank transfer), and the statement (every online payment, with the
// QPS Hub platform fee and processing spelled out line by line). The fee is
// shown here, to the person who agreed to it, and nowhere a customer can see.
export default function OnlinePayments() {
  const { authHeaders } = useApp();
  const [status, setStatus] = useState(null);
  const [statement, setStatement] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/connect/status', authHeaders()).then(r => setStatus(r.data)).catch(() => {});
    axios.get('/api/pay/statement/mine', authHeaders()).then(r => setStatement(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = async (path, body) => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(path, body || {}, authHeaders());
      window.location.href = r.data.url;
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setBusy(false);
    }
  };

  if (!status) return null;
  const pct = status.rates?.platformPct ?? 10;
  const rows = statement?.rows || [];

  return (
    <div style={{ padding: '16px 16px 0' }}>
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Get paid online</div>

        {/* The first thing on this screen, because the screen reads like a
            bill otherwise. Julian's rule: the estimates and invoices a
            technician writes for their own customers are free and need no
            Stripe. This is only about money reaching their bank. */}
        <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55, marginBottom: 10 }}>
          Writing estimates and invoices for your own customers is free, and nothing on this
          screen is needed for it.
        </div>

        {!status.configured && (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>Online payments are coming soon.</div>
        )}

        {status.configured && status.chargesEnabled && (
          <>
            <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
              On. Your customers can pay your invoices by bank transfer or card.
            </div>
            {!status.payoutsEnabled && (
              <div style={{ fontSize: 12.5, color: 'var(--amber)', marginTop: 6 }}>
                Stripe is still checking your bank details. Payments are held until it finishes.
              </div>
            )}
            <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }} disabled={busy} onClick={() => go('/api/connect/dashboard')}>
              Payouts &amp; bank details
            </button>
          </>
        )}

        {status.configured && !status.chargesEnabled && (
          <>
            <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.55 }}>
              Set this up for two things: to let your own customers pay your invoices online by bank transfer
              or card, and so QPS Hub can pay you for any work we send you. Either way the money goes
              straight to your bank. No monthly fee, and nothing to pay unless you get paid.
            </div>
            <PlatformFeeExplainer pct={pct} />
            {!status.termsAcceptedAt && (
              <PlatformFeeAgreement checked={agree} onChange={setAgree} pct={pct} />
            )}
            <button className="btn btn-primary" disabled={busy || (!status.termsAcceptedAt && !agree)}
              onClick={() => go('/api/connect/onboard', { acceptTerms: true })}>
              {busy ? 'Opening Stripe…' : status.connected ? 'Finish setting up' : 'Connect my bank with Stripe'}
            </button>
            <StripeHandoffNote />
          </>
        )}
        {error && <div style={{ color: 'var(--red)', fontSize: 12.5, marginTop: 8 }}>{error}</div>}
      </div>

      {rows.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Online payment statement</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            {[
              ['Customers paid', statement.totals.clientPaid],
              ['QPS Hub fees', statement.totals.platformFee],
              ['Processing', statement.totals.processingFee],
              ['You received', statement.totals.youReceive],
              ...(statement.totals.chargedBack > 0 ? [['Charged back', statement.totals.chargedBack]] : []),
            ].map(([l, v]) => (
              <div key={l} style={{ flex: 1, minWidth: 110 }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>{l}</div>
                <div style={{ fontSize: 15 }}>{fmtMoney(v)}</div>
              </div>
            ))}
          </div>
          {rows.map(r => (
            <div key={r._id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0', fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <strong>{r.job}{r.workOrder ? ` · ${r.workOrder}` : ''}</strong>
                <span style={{ color: 'var(--muted)', fontSize: 12 }}>{r.date ? fmtDate(r.date) : ''}</span>
              </div>
              {r.address && <div style={{ color: 'var(--muted)', fontSize: 12 }}>{r.address}</div>}
              <div style={{ marginTop: 6, lineHeight: 1.7 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Customer paid ({r.method}){r.status === 'pending' ? ', clearing' : ''}</span><span>{fmtMoney(r.clientPaid)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--slate)' }}>
                  <span>{`${r.platformPct ?? pct}% fee for using the QPS Hub platform`}</span><span>−{fmtMoney(r.platformFee)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--slate)' }}>
                  <span>Card / bank processing</span><span>−{fmtMoney(r.processingFee)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: r.dispute?.status === 'lost' ? 'var(--muted)' : r.status === 'pending' ? 'var(--amber)' : 'var(--green)', textDecoration: r.dispute?.status === 'lost' ? 'line-through' : 'none' }}>
                  <span>{r.status === 'pending' ? 'You will receive' : 'You received'}</span><span>{fmtMoney(r.youReceive)}</span>
                </div>
                {/* A chargeback, in the one place the technician looks for
                    where their money went. Never a silent subtraction. */}
                {r.dispute?.status === 'open' && (
                  <div style={{ marginTop: 6, padding: '8px 10px', background: 'var(--amber-light)', borderLeft: '3px solid var(--amber)', borderRadius: 'var(--radius-xs)', fontSize: 12.5, lineHeight: 1.55 }}>
                    <strong>The customer disputed this payment with their bank.</strong>{r.dispute.reason ? ` Reason given: ${r.dispute.reason}.` : ''}
                    {' '}Nothing has been taken from you. Send us photos of the finished work and anything the customer signed,
                    today. We cannot fight it after the bank's deadline.
                  </div>
                )}
                {r.dispute?.status === 'won' && (
                  <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--green)' }}>
                    The dispute was decided in your favour. The money is yours.
                  </div>
                )}
                {r.dispute?.status === 'lost' && (
                  <div style={{ marginTop: 6, padding: '8px 10px', background: 'var(--red-light)', borderLeft: '3px solid var(--red)', borderRadius: 'var(--radius-xs)', fontSize: 12.5, lineHeight: 1.55 }}>
                    <strong>The bank sided with the customer.</strong> The money went back to them.
                    {r.dispute.takenBack > 0
                      ? ` ${fmtMoney(r.dispute.takenBack)} was taken back out of your Stripe balance, which covers your share and the bank's dispute fee of ${fmtMoney(r.dispute.feeAmount)}.`
                      : ' Your share is owed back to QPS Hub. We will be in touch.'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
